import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand,
} from '@aws-sdk/client-cognito-identity';

export interface AwsCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string | undefined;
}

export interface AwsCredentialsConfig {
  identityPoolId: string;
  idsrvHost: string;
}

/**
 * Exchange an OIDC id_token for temporary AWS credentials via Cognito Identity Pool.
 * Framework-agnostic: Pass a getIdToken callback that returns the current OIDC id_token.
 *
 * Required integration step: Call invalidate() when the user logs out, or cached AWS
 * credentials remain usable for up to ~55 minutes after logout.
 */
export class AwsCredentialsService {
  /**
   * This is the buffer time in milliseconds before the real credentials expiry at which
   * we consider credentials stale for on-demand consumers and trigger a refresh.
   * CredentialsRefreshService has its own expiry buffer for proactive refreshes.
   */
  private readonly ON_DEMAND_REFRESH_EXPIRY_BUFFER_MS = 5 * 60 * 1000;

  private credentials: AwsCredentials | null = null;
  private expiry: Date | null = null;
  private inflight: Promise<AwsCredentials> | null = null;

  // Bumped by invalidate() so a fetch already in flight when logout happens can't
  // repopulate the cache with a now-logged-out user's credentials.
  private generation = 0;

  constructor(
    private readonly config: AwsCredentialsConfig,
    private readonly getIdToken: () => Promise<string>
  ) {}

  get hasFreshCredentials(): boolean {
    return (
      !!this.credentials &&
      !!this.expiry &&
      Date.now() <
        this.expiry.getTime() - this.ON_DEMAND_REFRESH_EXPIRY_BUFFER_MS
    );
  }

  async getCredentials(): Promise<AwsCredentials> {
    if (this.hasFreshCredentials && this.credentials) {
      return this.credentials;
    }

    return this.refresh();
  }

  /**
   * Force a credential fetch regardless of cache validity (proactive/visibility refresh).
   * Single-flight: Concurrent callers (timers, visibilitychange, in-flight requests) coalesce into
   * one Cognito call, so a caller that hits the refresh boundary waits briefly rather than failing.
   */
  refresh(): Promise<AwsCredentials> {
    if (!this.inflight) {
      const generation = this.generation;
      const pending = this.fetch(generation).finally(() => {
        // Only clear if a newer refresh (e.g. after invalidate) hasn't replaced it,
        // so that a settling stale fetch can't clear the current in-flight fetch
        if (this.inflight === pending) {
          this.inflight = null;
        }
      });
      this.inflight = pending;
    }

    return this.inflight;
  }

  /** Get the real AWS expiry of the cached credentials, or null if no credentials are cached. */
  get expiresAt(): Date | null {
    return this.expiry;
  }

  /**
   * Synchronously read the cached credentials, regardless of freshness, without triggering a refresh/fetch.
   * @returns the cached credentials, or null if no credentials have been fetched yet
   */
  get currentCredentials(): AwsCredentials | null {
    return this.credentials;
  }

  /**
   * Invalidate the cached credentials.
   *
   * Call this on logout or when a 403 response (stale credentials) is received.
   *
   * Also clear any in-flight fetch so that a post-invalidate refresh (e.g. new login or 403 retry)
   * can't coalesce into the previous session's in-flight fetch.
   */
  invalidate(): void {
    this.credentials = null;
    this.expiry = null;
    this.inflight = null;
    this.generation++;
  }

  /**
   * Get the region, derived from the pool ID prefix (e.g. "ca-central-1:ab901a31-...").
   * Public so callers (e.g. sigv4-link's resolveRegion fallback) don't need to reach into
   * the private config field.
   */
  get region(): string {
    return this.config.identityPoolId.split(':')[0];
  }

  private async fetch(generation: number): Promise<AwsCredentials> {
    const token = await this.getIdToken();
    if (!token) {
      throw new Error(
        'AwsCredentialsService: OIDC id_token is empty — user may not be authenticated'
      );
    }

    // Do NOT use the awsRegion config field — it is incorrect for the ca environment.
    const client = new CognitoIdentityClient({ region: this.region });

    const loginKey = this.config.idsrvHost;

    const { IdentityId } = await client.send(
      new GetIdCommand({
        IdentityPoolId: this.config.identityPoolId,
        Logins: { [loginKey]: token },
      })
    );

    if (!IdentityId) {
      throw new Error('AwsCredentialsService: GetId returned no IdentityId');
    }

    const { Credentials } = await client.send(
      new GetCredentialsForIdentityCommand({
        IdentityId,
        Logins: { [loginKey]: token },
      })
    );

    // Cognito Identity returns the secret as SecretKey, not SecretAccessKey (the STS field name).
    if (!Credentials?.AccessKeyId || !Credentials.SecretKey) {
      throw new Error(
        'AwsCredentialsService: GetCredentialsForIdentity returned incomplete credentials'
      );
    }

    const credentials: AwsCredentials = {
      accessKeyId: Credentials.AccessKeyId,
      secretAccessKey: Credentials.SecretKey,
      sessionToken: Credentials.SessionToken,
    };

    // invalidate() (e.g. logout) fired while this fetch was in flight. Return the credentials
    // to this caller, but don't let them repopulate the shared cache.
    if (generation !== this.generation) {
      return credentials;
    }

    this.credentials = credentials;

    // Cognito credentials default TTL is 1 hour
    const expirationMs = Credentials.Expiration
      ? Credentials.Expiration.getTime()
      : Date.now() + 60 * 60 * 1000;
    this.expiry = new Date(expirationMs);

    return this.credentials;
  }
}
